import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import type { Slot, PollInsights, VoteType } from "../../lib/insights/types";
import type { Participant } from "./ResultsTable";

interface ResultsOverviewProps {
  slots: Slot[];
  participants: Participant[];
  insights: PollInsights;
  onExit: () => void;
}

const CELL = 28; // px par colonne (ultra-dense)
const NAME = 140; // px col participant

/** "lun. 25 mai" → "L 25" — ultra-compact */
function compactDay(dateIso: string): string {
  const [y, m, d] = dateIso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const letters = ["D", "L", "M", "M", "J", "V", "S"];
  return `${letters[dt.getDay()]} ${d}`;
}

function compactTime(slot: Slot): string {
  const ts = slot.timeSlots?.[0];
  if (!ts) return "—";
  return `${ts.hour}h`;
}

function cellBg(v: VoteType | null | undefined): string {
  if (v === "yes") return "bg-vote-available";
  if (v === "maybe") return "bg-vote-maybe";
  if (v === "no") return "bg-vote-unavailable";
  return "";
}

/**
 * Mode "Vue d'ensemble" : matrice ultra-dense pour tenir la totalité des slots
 * × participants sans scroll horizontal. Cellules 14×14, day banner 1 lettre.
 * Cf. Results Final.html lignes 321-408.
 */
const ResultsOverview: React.FC<ResultsOverviewProps> = ({
  slots,
  participants,
  insights,
  onExit,
}) => {
  const { t } = useTranslation("polls");
  const winnerId = insights.winnerSlotId;

  // Groupage par jour pour la day-banner row
  const dayGroups = React.useMemo(() => {
    const groups: Array<{ date: string; span: number }> = [];
    for (const s of slots) {
      const last = groups[groups.length - 1];
      if (last && last.date === s.primaryDate) last.span += 1;
      else groups.push({ date: s.primaryDate, span: 1 });
    }
    return groups;
  }, [slots]);

  const slotIsNewDay = React.useMemo(() => {
    const out = new Array(slots.length).fill(false);
    for (let i = 0; i < slots.length; i++) {
      if (i === 0) {
        out[i] = false;
        continue;
      }
      out[i] = slots[i].primaryDate !== slots[i - 1].primaryDate;
    }
    return out;
  }, [slots]);

  const winnerDay = React.useMemo(() => {
    if (!winnerId) return null;
    return slots.find((s) => s.id === winnerId)?.primaryDate ?? null;
  }, [slots, winnerId]);

  const scoreById = React.useMemo(() => {
    const m = new Map<string, { yes: number }>();
    for (const sc of insights.scores) m.set(sc.slotId, { yes: sc.yes });
    return m;
  }, [insights.scores]);

  const totalVoters = insights.participantsCount;
  const gridStyle = {
    gridTemplateColumns: `${NAME}px repeat(${slots.length}, ${CELL}px)`,
  } as React.CSSProperties;

  return (
    <div data-testid="results-overview" className="space-y-3">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          {t("slot_filter_overview")}
        </h2>
        <span className="text-xs text-muted-foreground">
          {t("results_overview_summary", {
            voters: participants.length,
            slots: slots.length,
          })}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          data-testid="overview-exit"
          onClick={onExit}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          {t("results_overview_back")}
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-auto">
        {/* Day banner row */}
        <div
          className="grid bg-muted/40 border-b border-border"
          style={gridStyle}
        >
          <div className="px-3 py-2 text-[9px] uppercase tracking-wide font-semibold text-muted-foreground/70">
            {t("results_overview_participants")}
          </div>
          {dayGroups.map((g, gi) => {
            const hot = winnerDay === g.date;
            return (
              <div
                key={g.date}
                style={{ gridColumn: `span ${g.span}` }}
                className={`py-1.5 text-center text-[9px] font-semibold ${hot ? "text-vote-available-foreground" : "text-muted-foreground"} ${gi === 0 ? "" : "border-l border-border"}`}
              >
                {compactDay(g.date)}
              </div>
            );
          })}
        </div>

        {/* Time sub-header */}
        <div
          className="grid bg-muted/40 border-b border-border"
          style={gridStyle}
        >
          <div />
          {slots.map((s, i) => {
            const isWin = s.id === winnerId;
            const newDay = slotIsNewDay[i];
            return (
              <div
                key={s.id}
                title={`${s.primaryDate} ${compactTime(s)}`}
                className={`py-1 text-center text-[8px] font-medium ${isWin ? "text-vote-available-foreground font-bold" : "text-muted-foreground"} ${newDay && i > 0 ? "border-l border-border" : ""} ${isWin ? "bg-vote-available/[0.10]" : ""}`}
              >
                {compactTime(s)}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="max-h-[380px] overflow-y-auto">
          {participants.map((p, pi) => (
            <div
              key={p.id}
              data-testid={`overview-row-${p.id}`}
              data-you={p.isYou ? "true" : undefined}
              className={`grid ${pi === participants.length - 1 ? "" : "border-b border-border/60"} ${p.isYou ? "bg-primary/[0.06]" : ""}`}
              style={gridStyle}
            >
              <div className="px-3 py-1 flex items-center gap-2 min-w-0">
                <span
                  className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-semibold uppercase`}
                  aria-hidden
                >
                  {(p.name || "?").charAt(0)}
                </span>
                <span
                  className={`text-[11.5px] ${p.isYou ? "font-semibold" : "font-medium"} truncate`}
                  title={p.name}
                >
                  {p.isYou ? t("you") : p.name}
                </span>
              </div>
              {slots.map((s, i) => {
                const v = p.votes[s.id];
                const isWin = s.id === winnerId;
                const newDay = slotIsNewDay[i];
                return (
                  <div
                    key={s.id}
                    data-testid={`overview-cell-${p.id}-${s.id}`}
                    data-vote={v ?? "empty"}
                    className={`flex items-center justify-center py-1 ${newDay && i > 0 ? "border-l border-border" : ""} ${isWin ? "bg-vote-available/[0.04]" : ""}`}
                  >
                    {v ? (
                      <div
                        className={`h-[14px] w-[14px] rounded-[3px] ${cellBg(v)}`}
                        aria-label={`${p.name} ${v}`}
                      />
                    ) : (
                      <span className="text-muted-foreground/50 text-[9px]">
                        ·
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Yes-count footer */}
        <div
          className="grid bg-muted/40 border-t border-border"
          style={gridStyle}
        >
          <div className="px-3 py-1.5 text-[9px] uppercase tracking-wide font-semibold text-muted-foreground/70">
            Oui
          </div>
          {slots.map((s, i) => {
            const isWin = s.id === winnerId;
            const newDay = slotIsNewDay[i];
            const yes = scoreById.get(s.id)?.yes ?? 0;
            const hot = totalVoters > 0 && yes >= totalVoters * 0.5;
            return (
              <div
                key={s.id}
                data-testid={`overview-yes-${s.id}`}
                className={`relative py-1 text-center text-[11px] font-bold tabular-nums ${isWin ? "text-vote-available-foreground bg-vote-available/[0.12]" : hot ? "text-foreground" : "text-muted-foreground"} ${newDay && i > 0 ? "border-l border-border" : ""}`}
              >
                {isWin && (
                  <div
                    className="absolute inset-x-0 top-0 h-[1.5px] bg-vote-available"
                    aria-hidden
                  />
                )}
                {yes}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ResultsOverview;
